import React, { useEffect, useMemo, useState } from 'react';
import { Stack, Table } from 'react-bootstrap';
import { useAuth } from 'providers/auth-provider';
import { Link } from 'react-router-dom';
import { useAssessmentsDataContext } from 'providers/assessments-data-provider';
import { IconContext } from 'react-icons';
import { BsPencilSquare } from 'react-icons/bs';
import { FaRegCheckCircle, FaRegTimesCircle } from 'react-icons/fa';
import { getAssessmentStatus } from '../../api/assessments-api';
import LoadingSpinner from '../../components/common/loading-spinner';
// eslint-disable-next-line import/max-dependencies
import { COMPLETION_REDIRECT_URL } from '../../config/constants';

const styles = {
  row: {
    height: '4em',
  },
  notStarted: {
    backgroundColor: '#f7c3af',
    fontWeight: '600',
    color: '#ea6434',
  },
  inProcess: {
    backgroundColor: '#9cdc94',
    fontWeight: '600',
    color: '#1e8055',
  },
  complete: {
    backgroundColor: '#c9ebf7',
    fontWeight: '600',
    color: '#5cbfe6',
  },
};

function Assessments() {
  const {
    selfPacedAssessments,
    wmAssessments,
    selfCompetenciesAssessments,
    raterCompetenciesAssessments,
    getAssessments,
    assigned,
    assignedAssessments,
    assessmentsRetrieved,
  } = useAssessmentsDataContext();

  const notStartedProps = useMemo(() => ({ color: '#ea6434', className: 'global-class-name' }), []);
  const inProcessProps = useMemo(() => ({ color: '#1e8055', className: 'global-class-name' }), []);
  const completedProps = useMemo(() => ({ color: '#5cbfe6', className: 'global-class-name' }), []);

  const auth = useAuth();

  const [workPreferencesTime, setWorkPreferencesTime] = useState(20);

  const [selfPacedStyle, setSelfPacedStyle] = useState(styles.notStarted);
  const [selfPacedText, setSelfPacedText] = useState('Not started');
  const [selfPacedStarted, setSelfPacedStarted] = useState(false);
  const [selfPacedComplete, setSelfPacedComplete] = useState(false);
  const [selfPacedIconProps, setSelfPacedIconProps] = useState(notStartedProps);

  const [wmStyle, setWmStyle] = useState(styles.notStarted);
  const [wmText, setWmText] = useState('Not started');
  const [wmStarted, setWmStarted] = useState(false);
  const [wmComplete, setWmComplete] = useState(false);
  const [wmIconProps, setWmIconProps] = useState(notStartedProps);

  const [selfCompetenciesStyle, setSelfCompetenciesStyle] = useState(styles.notStarted);
  const [selfCompetenciesTitle, setSelfCompetenciesTitle] = useState('Competencies');
  const [selfCompetenciesText, setSelfCompetenciesText] = useState('Not started');
  const [selfCompetenciesStarted, setSelfCompetenciesStarted] = useState(false);
  const [selfCompetenciesComplete, setSelfCompetenciesComplete] = useState(false);
  const [selfCompetenciesIconProps, setSelfCompetenciesIconProps] = useState(notStartedProps);

  const [raterAssignments, setRaterAssignments] = useState([]);

  const pencilProps = useMemo(() => ({ color: 'black', className: 'global-class-name' }), []);
  const mouseEnter = (e) => {
    const event = { ...e };
    event.target.style.color = 'blue';
    event.target.style.cursor = 'pointer';
  };

  const mouseLeave = (e) => {
    const event = { ...e };
    event.target.style.color = '';
    event.target.style.cursor = '';
  };

  useEffect(() => {
    if (COMPLETION_REDIRECT_URL) {
      if (selfPacedStarted && wmStarted && selfCompetenciesStarted) {
        if (selfPacedComplete && wmComplete && selfCompetenciesStarted) {
          window.location.href = COMPLETION_REDIRECT_URL;
        }
      }
    }
  }, [
    selfPacedComplete,
    selfPacedStarted,
    wmComplete,
    wmStarted,
    selfCompetenciesComplete,
    selfCompetenciesStarted,
    raterAssignments,
  ]);

  useEffect(() => {
    if (assessmentsRetrieved) {
      if (selfPacedAssessments && selfPacedAssessments.length > 0) {
        setSelfPacedText('Not started');
        setSelfPacedIconProps(notStartedProps);
        setSelfPacedStyle(styles.notStarted);
        setSelfPacedComplete(false);
        for (let a = 0; a < selfPacedAssessments.length; a += 1) {
          const status = getAssessmentStatus(selfPacedAssessments[a]);
          if (status.expired) {
            setSelfPacedStarted(true);
            setSelfPacedComplete(true);
            setSelfPacedText('Expired');
            break;
          }
          if (status.started) {
            setSelfPacedStarted(true);
            setSelfPacedText('In Process');
            setSelfPacedIconProps(inProcessProps);
            setSelfPacedStyle(styles.inProcess);
            break;
          }
        }
      } else {
        setSelfPacedText('Complete');
        setSelfPacedIconProps(completedProps);
        setSelfPacedStyle(styles.complete);
        setSelfPacedStarted(true);
        setSelfPacedComplete(true);
      }

      if (wmAssessments && wmAssessments.length > 0) {
        setWmText('Not started');
        setWmIconProps(notStartedProps);
        setWmStyle(styles.notStarted);
        setWmComplete(false);
        for (let a = 0; a < wmAssessments.length; a += 1) {
          const status = getAssessmentStatus(wmAssessments[a]);
          if (status.started || status.expired) {
            setWmStarted(true);
            setWmComplete(true);
            setWmText('Expired');
            break;
          }
        }
      } else {
        setWmText('Complete');
        setWmIconProps(completedProps);
        setWmStyle(styles.complete);
        setWmStarted(true);
        setWmComplete(true);
      }

      if (selfCompetenciesAssessments && selfCompetenciesAssessments.length > 0) {
        setSelfCompetenciesText('Not started');
        setSelfCompetenciesIconProps(notStartedProps);
        setSelfCompetenciesStyle(styles.notStarted);
        setSelfCompetenciesComplete(false);
        for (let a = 0; a < selfCompetenciesAssessments.length; a += 1) {
          const status = getAssessmentStatus(selfCompetenciesAssessments[a]);
          setSelfCompetenciesTitle(selfCompetenciesAssessments[a].title);
          if (status.expired) {
            setSelfCompetenciesStarted(true);
            setSelfCompetenciesComplete(true);
            setSelfCompetenciesText('Expired');
            break;
          }
          if (status.started) {
            setSelfCompetenciesStarted(true);
            setSelfCompetenciesIconProps(inProcessProps);
            setSelfCompetenciesText('In Process');
            setSelfCompetenciesStyle(styles.inProcess);
            break;
          }
        }
      } else {
        setSelfCompetenciesText('Complete');
        setSelfCompetenciesIconProps(completedProps);
        setSelfCompetenciesStyle(styles.complete);
        setSelfCompetenciesStarted(true);
        setSelfCompetenciesComplete(true);
      }

      if (raterCompetenciesAssessments && raterCompetenciesAssessments.length > 0) {
        const assignments = [];
        for (let a = 0; a < raterCompetenciesAssessments.length; a += 1) {
          const raterAssignmentState = {
            text: 'Not started',
            iconProps: notStartedProps,
            style: styles.notStarted,
            started: false,
            complete: false,
            title: raterCompetenciesAssessments[a].title,
          };
          assignments.push(raterAssignmentState);

          const status = getAssessmentStatus(raterCompetenciesAssessments[a]);
          if (status.expired) {
            raterAssignmentState.started = true;
            raterAssignmentState.complete = true;
            raterAssignmentState.text = 'Expired';
            continue;
          }
          if (status.submitted) {
            raterAssignmentState.started = true;
            raterAssignmentState.complete = true;
            raterAssignmentState.text = 'Complete';
            raterAssignmentState.iconProps = completedProps;
            raterAssignmentState.style = styles.complete;
            continue;
          }
          if (status.started) {
            raterAssignmentState.started = true;
            raterAssignmentState.text = 'In Process';
            raterAssignmentState.iconProps = inProcessProps;
            raterAssignmentState.style = styles.inProcess;
          }
        }
        setRaterAssignments(assignments);
      }
    }
  }, [
    assessmentsRetrieved,
    selfPacedAssessments,
    wmAssessments,
    selfCompetenciesAssessments,
    raterCompetenciesAssessments,
  ]);

  useEffect(() => {
    getAssessments(auth);
    if (auth.slug === 'prolific') {
      setWorkPreferencesTime(40);
    }
  }, [auth, getAssessments]);

  return (
    <div className="flex-row bg-gray-200">
      {!assessmentsRetrieved && <LoadingSpinner />}
      <Stack gap={1} className="p-5 w-100 mx-auto assessment-container">
        <div className="flex-row mx-auto justify-content-center">
          <div className="d-flex justify-content-center pb-1 assessment-display-name">{auth.userDisplayName}</div>
          <div className="d-flex justify-content-center pb-1 assessment-slug">{auth.clientDisplayName}</div>
          <div className="d-flex justify-content-center pb-4 assessment-id">{`ID - ${auth.clientId}`}</div>
        </div>
        {assessmentsRetrieved && (
          <>
            <Stack className="mx-auto bg-white p-4 d-none d-md-block" style={{ width: '90%' }}>
              <Table striped>
                <thead>
                  <tr>
                    <th>Assessments</th>
                    <th>Time to Complete</th>
                    <th>Status</th>
                    <th>Date Assigned</th>
                    <th />
                  </tr>
                </thead>
                <tbody className="align-middle">
                  {assignedAssessments.selfPaced && (
                    <tr style={styles.row}>
                      <td>
                        <div className="text-bolder">Work Preferences</div>
                      </td>
                      <td>{workPreferencesTime} minutes</td>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={selfPacedStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={selfPacedIconProps}>
                              <div>
                                {!selfPacedStarted && <FaRegTimesCircle />}
                                {selfPacedStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{selfPacedText}</div>
                        </div>
                      </td>
                      <td>{assigned}</td>
                      <td className="text-center">
                        {!selfPacedComplete && (
                          <Link to="assessments/work-preferences">
                            <div className="d-flex justify-content-start">
                              <div>
                                <IconContext.Provider value={pencilProps}>
                                  <div>
                                    <BsPencilSquare />
                                  </div>
                                </IconContext.Provider>
                              </div>
                              <div
                                style={{ paddingLeft: '1em' }}
                                onMouseOver={(event) => {
                                  mouseEnter(event);
                                }}
                                onMouseOut={(event) => {
                                  mouseLeave(event);
                                }}
                              >
                                Take Assessment
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                    </tr>
                  )}
                  {assignedAssessments.workingMemory && (
                    <tr style={styles.row}>
                      <td className="text-bolder">Acumen</td>
                      <td>10 minutes</td>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={wmStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={wmIconProps}>
                              <div>
                                {!wmStarted && <FaRegTimesCircle />}
                                {wmStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{wmText}</div>
                        </div>
                      </td>
                      <td>{assigned}</td>
                      <td className="text-center">
                        {!wmComplete && (
                          <Link to="assessments/acumen">
                            <div className="d-flex justify-content-start">
                              <div>
                                <IconContext.Provider value={pencilProps}>
                                  <div>
                                    <BsPencilSquare />
                                  </div>
                                </IconContext.Provider>
                              </div>
                              <div
                                style={{ paddingLeft: '1em' }}
                                onMouseOver={(event) => {
                                  mouseEnter(event);
                                }}
                                onMouseOut={(event) => {
                                  mouseLeave(event);
                                }}
                              >
                                Take Assessment
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                    </tr>
                  )}
                  {assignedAssessments.selfCompetencies && (
                    <tr style={styles.row}>
                      <td>
                        <div className="text-bolder">{selfCompetenciesTitle}</div>
                      </td>
                      <td>7 minutes</td>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={selfCompetenciesStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={selfCompetenciesIconProps}>
                              <div>
                                {!selfCompetenciesStarted && <FaRegTimesCircle />}
                                {selfCompetenciesStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{selfCompetenciesText}</div>
                        </div>
                      </td>
                      <td>{assigned}</td>
                      <td className="text-center">
                        {!selfCompetenciesComplete && (
                          <Link to="assessments/competencies/self">
                            <div className="d-flex justify-content-start">
                              <div>
                                <IconContext.Provider value={pencilProps}>
                                  <div>
                                    <BsPencilSquare />
                                  </div>
                                </IconContext.Provider>
                              </div>
                              <div
                                style={{ paddingLeft: '1em' }}
                                onMouseOver={(event) => {
                                  mouseEnter(event);
                                }}
                                onMouseOut={(event) => {
                                  mouseLeave(event);
                                }}
                              >
                                Take Assessment
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                    </tr>
                  )}
                  {raterAssignments.map((raterAssignment, index) => (
                    <tr style={styles.row} key={index}>
                      <td>
                        <div className="text-bolder">{raterAssignment.title}</div>
                      </td>
                      <td>7 minutes</td>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={raterAssignment.style}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={raterAssignment.iconProps}>
                              <div>
                                {!raterAssignment.started && <FaRegTimesCircle />}
                                {raterAssignment.started && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{raterAssignment.text}</div>
                        </div>
                      </td>
                      <td>{assigned}</td>
                      <td className="text-center">
                        {!raterAssignment.complete && (
                          <Link to={`assessments/competencies/rater/${index}`}>
                            <div className="d-flex justify-content-start">
                              <div>
                                <IconContext.Provider value={pencilProps}>
                                  <div>
                                    <BsPencilSquare />
                                  </div>
                                </IconContext.Provider>
                              </div>
                              <div
                                style={{ paddingLeft: '1em' }}
                                onMouseOver={(event) => {
                                  mouseEnter(event);
                                }}
                                onMouseOut={(event) => {
                                  mouseLeave(event);
                                }}
                              >
                                Take Assessment
                              </div>
                            </div>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Stack>
            <Stack className="mx-auto bg-white d-md-none" style={{ width: '100%', fontSize: '0.8em' }}>
              {assignedAssessments.selfPaced && (
                <Table cellPadding="17" bordered>
                  <tbody>
                    <tr>
                      <th>Assessment</th>
                      <td className="text-bolder">Work Preferences</td>
                    </tr>
                    <tr>
                      <th>Time to Complete</th>
                      <td>{workPreferencesTime} Minutes</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={selfPacedStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={selfPacedIconProps}>
                              <div>
                                {!selfPacedStarted && <FaRegTimesCircle />}
                                {selfPacedStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{selfPacedText}</div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Date Assigned</th>
                      <td>
                        <div>{assigned}</div>
                      </td>
                    </tr>
                    <tr>
                      <td />
                      <td>
                        <div className="text-center">
                          {!selfPacedComplete && (
                            <Link to="assessments/work-preferences">
                              <div className="d-flex justify-content-start">
                                <div>
                                  <IconContext.Provider value={pencilProps}>
                                    <div>
                                      <BsPencilSquare />
                                    </div>
                                  </IconContext.Provider>
                                </div>
                                <div
                                  style={{ paddingLeft: '1em' }}
                                  onMouseOver={(event) => {
                                    mouseEnter(event);
                                  }}
                                  onMouseOut={(event) => {
                                    mouseLeave(event);
                                  }}
                                >
                                  Take Assessment
                                </div>
                              </div>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
              {assignedAssessments.workingMemory && (
                <Table cellPadding="17" bordered className="mt-3" style={{ backgroundColor: '#F4F5F7' }}>
                  <tbody>
                    <tr>
                      <th>Assessment</th>
                      <td className="text-bolder">Acumen</td>
                    </tr>
                    <tr>
                      <th>Time to Complete</th>
                      <td>10 Minutes</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={wmStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={wmIconProps}>
                              <div>
                                {!wmStarted && <FaRegTimesCircle />}
                                {wmStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{wmText}</div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Date Assigned</th>
                      <td>
                        <div>{assigned}</div>
                      </td>
                    </tr>
                    <tr>
                      <td />
                      <td>
                        <div className="text-center">
                          {!wmComplete && (
                            <Link to="assessments/acumen">
                              <div className="d-flex justify-content-start">
                                <div>
                                  <IconContext.Provider value={pencilProps}>
                                    <div>
                                      <BsPencilSquare />
                                    </div>
                                  </IconContext.Provider>
                                </div>
                                <div
                                  style={{ paddingLeft: '1em' }}
                                  onMouseOver={(event) => {
                                    mouseEnter(event);
                                  }}
                                  onMouseOut={(event) => {
                                    mouseLeave(event);
                                  }}
                                >
                                  Take Assessment
                                </div>
                              </div>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
              {assignedAssessments.selfCompetencies && (
                <Table cellPadding="17" bordered className="mt-3">
                  <tbody>
                    <tr>
                      <th>Assessment</th>
                      <td className="text-bolder">Competencies</td>
                    </tr>
                    <tr>
                      <th>Time to Complete</th>
                      <td>7 Minutes</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={selfCompetenciesStyle}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={selfCompetenciesIconProps}>
                              <div>
                                {!selfCompetenciesStarted && <FaRegTimesCircle />}
                                {selfCompetenciesStarted && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{selfCompetenciesText}</div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Date Assigned</th>
                      <td>
                        <div>{assigned}</div>
                      </td>
                    </tr>
                    <tr>
                      <td />
                      <td>
                        <div className="text-center">
                          {!selfCompetenciesComplete && (
                            <Link to="assessments/competencies/self">
                              <div className="d-flex justify-content-start">
                                <div>
                                  <IconContext.Provider value={pencilProps}>
                                    <div>
                                      <BsPencilSquare />
                                    </div>
                                  </IconContext.Provider>
                                </div>
                                <div
                                  style={{ paddingLeft: '1em' }}
                                  onMouseOver={(event) => {
                                    mouseEnter(event);
                                  }}
                                  onMouseOut={(event) => {
                                    mouseLeave(event);
                                  }}
                                >
                                  Take Assessment
                                </div>
                              </div>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              )}
              {raterAssignments.map((raterAssignment, index) => (
                <Table cellPadding="17" bordered className="mt-3" key={index}>
                  <tbody>
                    <tr>
                      <th>Assessment</th>
                      <td className="text-bolder">{raterAssignment.title}</td>
                    </tr>
                    <tr>
                      <th>Time to Complete</th>
                      <td>7 Minutes</td>
                    </tr>
                    <tr>
                      <th>Status</th>
                      <td>
                        <div className="d-flex justify-content-start pt-2 pb-2" style={raterAssignment.style}>
                          <div style={{ marginTop: '-1px', paddingLeft: '0.5em' }}>
                            <IconContext.Provider value={raterAssignment.iconProps}>
                              <div>
                                {!raterAssignment.started && <FaRegTimesCircle />}
                                {raterAssignment.started && <FaRegCheckCircle />}
                              </div>
                            </IconContext.Provider>
                          </div>
                          <div style={{ paddingLeft: '1em', paddingRight: '1em' }}>{raterAssignment.text}</div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>Date Assigned</th>
                      <td>
                        <div>{assigned}</div>
                      </td>
                    </tr>
                    <tr>
                      <td />
                      <td>
                        <div className="text-center">
                          {!raterAssignment.complete && (
                            <Link to={`assessments/competencies/rater/${index}`}>
                              <div className="d-flex justify-content-start">
                                <div>
                                  <IconContext.Provider value={pencilProps}>
                                    <div>
                                      <BsPencilSquare />
                                    </div>
                                  </IconContext.Provider>
                                </div>
                                <div
                                  style={{ paddingLeft: '1em' }}
                                  onMouseOver={(event) => {
                                    mouseEnter(event);
                                  }}
                                  onMouseOut={(event) => {
                                    mouseLeave(event);
                                  }}
                                >
                                  Take Assessment
                                </div>
                              </div>
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </Table>
              ))}
            </Stack>
          </>
        )}
      </Stack>
    </div>
  );
}

export default Assessments;
